import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_7cce28e4-fe0a-4a9e-8874-d3a0b48d9685/artifacts/e1fqiqg9_logo-square-transparent-13ddc4b%20%281%29.png";
const BG_IMAGE = "https://images.unsplash.com/photo-1758518729685-f88df7890776?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxjb3Jwb3JhdGUlMjBvZmZpY2UlMjB0ZWFtJTIwbWVldGluZyUyMHByb2Zlc3Npb25hbHxlbnwwfHx8fDE3NzAzMTAyNDJ8MA&ixlib=rb-4.1.0&q=85";

const Login = () => {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('login');
  
  // Login form state
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  
  // Register form state
  const [registerName, setRegisterName] = useState('');
  const [registerEmail, setRegisterEmail] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [registerRole, setRegisterRole] = useState('Submitter');
  const [registerDepartment, setRegisterDepartment] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { user, companies } = await login(loginEmail, loginPassword);
      toast.success('Welcome back!');
      // Routing:
      //   - 1 company assigned → straight to dashboard
      //   - 0 or >1 companies → /select-company picker
      if (user.is_master_admin || (companies?.length || 0) !== 1) {
        navigate('/select-company');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { user, companies } = await register({
        name: registerName,
        email: registerEmail,
        password: registerPassword,
        role: registerRole,
        department: registerDepartment || null,
      });
      toast.success('Account created successfully!');
      if (user.is_master_admin || (companies?.length || 0) !== 1) {
        navigate('/select-company');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const fillDemoCredentials = (role) => {
    const credentials = {
      master: { email: 'fabio@beyondmeasure.global', password: 'GabRafAle@1' },
      admin: { email: 'admin@beyonddelta.com', password: 'admin123' },
      approver: { email: 'approver@beyonddelta.com', password: 'approver123' },
      submitter: { email: 'submitter@beyonddelta.com', password: 'submitter123' },
    };
    const cred = credentials[role];
    setLoginEmail(cred.email);
    setLoginPassword(cred.password);
  };

  // Show demo quick-login buttons ONLY in the preview environment, never
  // on the production deployment. Identified by the .preview. subdomain.
  const showDemoLogins = typeof window !== 'undefined'
    && window.location.hostname.includes('preview.emergentagent.com');

  return (
    <div className="min-h-screen flex">
      {/* Left side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md space-y-8 animate-fade-in">
          {/* Logo */}
          <div className="text-center">
            <img 
              src={LOGO_URL} 
              alt="Beyond Delta" 
              className="w-20 h-20 mx-auto mb-4"
            />
            <h1 className="text-3xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">
              Beyond Delta
            </h1>
            <p className="mt-2 text-slate-500">
              Continuous Improvement Platform
            </p>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="login" data-testid="login-tab">Login</TabsTrigger>
              <TabsTrigger value="register" data-testid="register-tab">Register</TabsTrigger>
            </TabsList>
            
            <TabsContent value="login">
              <Card>
                <CardHeader>
                  <CardTitle>Welcome back</CardTitle>
                  <CardDescription>Enter your credentials to access your account</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="login-email">Email</Label>
                      <Input
                        id="login-email"
                        type="email"
                        placeholder="you@example.com"
                        value={loginEmail}
                        onChange={(e) => setLoginEmail(e.target.value)}
                        required
                        data-testid="login-email-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="login-password">Password</Label>
                      <Input
                        id="login-password"
                        type="password"
                        placeholder="••••••••"
                        value={loginPassword}
                        onChange={(e) => setLoginPassword(e.target.value)}
                        required
                        data-testid="login-password-input"
                      />
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full bg-[#235894] hover:bg-[#1E4B80]"
                      disabled={loading}
                      data-testid="login-submit-btn"
                    >
                      {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      Sign In
                    </Button>
                  </form>

                  {/* Demo credentials (preview env only) */}
                  {showDemoLogins && (
                  <div className="mt-6 pt-6 border-t border-slate-200">
                    <p className="text-sm text-slate-500 mb-3">Quick login with demo accounts:</p>
                    <div className="flex flex-wrap gap-2">
                      <Button 
                        type="button" 
                        variant="outline" 
                        size="sm"
                        onClick={() => fillDemoCredentials('admin')}
                        data-testid="demo-admin-btn"
                      >
                        CI Manager
                      </Button>
                      <Button 
                        type="button" 
                        variant="outline" 
                        size="sm"
                        onClick={() => fillDemoCredentials('approver')}
                        data-testid="demo-approver-btn"
                      >
                        Approver
                      </Button>
                      <Button 
                        type="button" 
                        variant="outline" 
                        size="sm"
                        onClick={() => fillDemoCredentials('submitter')}
                        data-testid="demo-submitter-btn"
                      >
                        Submitter
                      </Button>
                    </div>
                  </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="register">
              <Card>
                <CardHeader>
                  <CardTitle>Create an account</CardTitle>
                  <CardDescription>Enter your details to get started</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleRegister} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="register-name">Full Name</Label>
                      <Input
                        id="register-name"
                        type="text"
                        placeholder="John Doe"
                        value={registerName}
                        onChange={(e) => setRegisterName(e.target.value)}
                        required
                        data-testid="register-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-email">Email</Label>
                      <Input
                        id="register-email"
                        type="email"
                        placeholder="you@example.com"
                        value={registerEmail}
                        onChange={(e) => setRegisterEmail(e.target.value)}
                        required
                        data-testid="register-email-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-password">Password</Label>
                      <Input
                        id="register-password"
                        type="password"
                        placeholder="••••••••"
                        value={registerPassword}
                        onChange={(e) => setRegisterPassword(e.target.value)}
                        required
                        data-testid="register-password-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-role">Role</Label>
                      <Select value={registerRole} onValueChange={setRegisterRole}>
                        <SelectTrigger data-testid="register-role-select">
                          <SelectValue placeholder="Select role" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Submitter">Submitter</SelectItem>
                          <SelectItem value="Approver">Approver</SelectItem>
                          <SelectItem value="CI Manager">CI Manager</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-department">Department (Optional)</Label>
                      <Input
                        id="register-department"
                        type="text"
                        placeholder="e.g., Operations"
                        value={registerDepartment}
                        onChange={(e) => setRegisterDepartment(e.target.value)}
                        data-testid="register-department-input"
                      />
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full bg-[#235894] hover:bg-[#1E4B80]"
                      disabled={loading}
                      data-testid="register-submit-btn"
                    >
                      {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      Create Account
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Right side - Image */}
      <div 
        className="hidden lg:block lg:w-1/2 bg-cover bg-center"
        style={{ backgroundImage: `url(${BG_IMAGE})` }}
      >
        <div className="h-full w-full bg-[#235894]/60 backdrop-blur-sm flex items-center justify-center p-12">
          <div className="text-white text-center max-w-lg">
            <h2 className="text-4xl font-bold font-['Plus_Jakarta_Sans'] mb-6">
              Drive Continuous Improvement
            </h2>
            <p className="text-xl text-white/90 leading-relaxed">
              Submit ideas, track projects, and measure the impact of your improvement initiatives across the organization.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
